import { createClient } from '@supabase/supabase-js';
import env from './loadEnv';
import { setupAuthTokenRefresh } from './supabaseAuth';

// Initialize Supabase client
const supabaseUrl = env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Setup automatic token refresh
setupAuthTokenRefresh(supabase);

// Initialize offline queue for operations that fail due to connectivity issues
let offlineQueue: Array<{
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  condition?: any;
}> = [];

// Check if we have stored operations in localStorage
const loadOfflineQueue = () => {
  try {
    const storedQueue = localStorage.getItem('mgp_offline_queue');
    if (storedQueue) {
      offlineQueue = JSON.parse(storedQueue);
    }
  } catch (error) {
    console.error('Error loading offline queue:', error);
  }
};

// Save queue to localStorage
const saveOfflineQueue = () => {
  try {
    localStorage.setItem('mgp_offline_queue', JSON.stringify(offlineQueue));
  } catch (error) {
    console.error('Error saving offline queue:', error);
  }
};

// Initialize by loading any stored operations
loadOfflineQueue();

// Process offline queue when online
const processOfflineQueue = async () => {
  if (offlineQueue.length === 0) return;

  console.log(`Processing ${offlineQueue.length} offline operations...`);

  // Process each operation in order
  const operations = [...offlineQueue];
  offlineQueue = [];
  saveOfflineQueue();

  for (const op of operations) {
    try {
      if (op.operation === 'insert') {
        await supabase.from(op.table).insert(op.data);
      } else if (op.operation === 'update') {
        await supabase.from(op.table).update(op.data).match(op.condition);
      } else if (op.operation === 'delete') {
        await supabase.from(op.table).delete().match(op.condition);
      }
    } catch (error) {
      console.error(`Error processing offline operation for ${op.table}:`, error);
      // Put failed operation back in queue
      offlineQueue.push(op);
      saveOfflineQueue();
    }
  }

  console.log('Offline queue processing complete');
};

// Listen for online status changes
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Back online, processing offline queue...');
    processOfflineQueue();
  });
}

// Wrapper for Supabase operations with offline support
export const supabaseWithOfflineSupport = {
  upsertScore: async (table: string, data: any, condition: any) => {
    try {
      // First check if the record exists
      const { data: existingData, error: checkError } = await supabase
        .from(table)
        .select('id')
        .match(condition)
        .limit(1);

      if (checkError) {
        console.error('Error checking for existing record:', checkError);
        throw checkError;
      }

      let error = null;

      // If record exists, update it
      if (existingData && existingData.length > 0) {
        const { error: updateError } = await supabase
          .from(table)
          .update(data)
          .match(condition);

        error = updateError;
      } else {
        // If record doesn't exist, insert it
        const { error: insertError } = await supabase
          .from(table)
          .insert(data);

        error = insertError;
      }

      if (error) {
        console.error('Error upserting to Supabase:', error);
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Error in upsertScore, adding to offline queue:', error);
      // Add to offline queue
      offlineQueue.push({
        table,
        operation: 'update', // Use update for offline queue since we'll handle the insert case separately
        data,
        condition,
      });
      saveOfflineQueue();
      return { error, isOffline: true };
    }
  },

  subscribeToGameScores: (gameId: string, callback: (payload: any) => void) => {
    return supabase
      .channel(`game-scores-${gameId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_scores',
        filter: `game_id=eq.${gameId}`,
      }, callback)
      .subscribe();
  },
};
